import { getUSANumberFormat, shouldShowDollarSymbol } from "app/utils/utils";
import { ProposalTypes } from "app/utils/types";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { getObjectFromLocalStore, removeLocalStore, setLocalStore } from "app/utils/localStore";
import { makeRequest } from "app/utils/makeRequest";
import { buildUIPAuthHeaders } from "./uipAuth";

// UIP Messaging API base URL - use env var or fallback to QA UIP
const NEIO_CORE_API_URL =
  process.env.REACT_APP_NEIO_CORE_API_URL || "https://qa-uip.iapp.cool/api/v1";

// UIP Workspace ID - required for all UIP messaging API calls
const getUIPWorkspaceId = (): string | null => {
  const value = getObjectFromLocalStore("uipWorkspaceId");
  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }
  if (value && typeof value === "object" && typeof value.id === "string") {
    return value.id;
  }
  return null;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  const value = bytes / k ** i;

  return i === 1
    ? `${Math.round(value)} ${sizes[i]}`
    : `${parseFloat(value.toFixed(2))} ${sizes[i]}`;
}

export function isSplitDollar(proposalType: ProposalTypes) {
  return proposalType === ProposalTypes.SplitDollar;
}

export function isKeyPersonInsurance(proposalType: ProposalTypes) {
  return proposalType === ProposalTypes.KeyPersonInsurance;
}

export function parseDateString(dateString: string): Date {
  const datePart = dateString?.split("T")[0]; // strip time
  const [year, month, day] = datePart.split("-").map(Number);
  // Create date in local timezone to avoid timezone conversion issues
  // Use noon (12:00) to prevent any timezone edge cases that could shift the date
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}
const safeBigIntToNumber = (value: number | bigint): number => {
  if (typeof value === "bigint") {
    return Number(value);
  }
  return value;
};
export const USFormatNumber = (value: number | bigint, category: string) => {
  const safeValue = safeBigIntToNumber(value);
  if (shouldShowDollarSymbol(category)) {
    return `$${getUSANumberFormat(safeValue, false)}`; // With $ symbol
  }
  return getUSANumberFormat(safeValue, false); // Without $ symbol, US format
};

/**
 * Get or create a direct conversation between two users via UIP API.
 * Uses POST /messaging/conversations/direct endpoint which creates if not exists.
 */
export const checkChatConversationExists = async (
  firstUserID?: string,
  secondUserID?: string,
  uipAccessToken?: string,
  firstUserType: string = "user",
  secondUserType: string = "bot",
) => {
  const workspaceId = getUIPWorkspaceId();
  if (!workspaceId) {
    console.error("UIP workspace ID not available");
    return { error: "Workspace ID not configured" };
  }

  // UIP uses POST /messaging/conversations/direct to get-or-create
  const url = `${NEIO_CORE_API_URL}/messaging/conversations/direct?workspace_id=${workspaceId}`;
  const getConversationResponse = await fetch(url, {
    method: "POST",
    headers: buildUIPAuthHeaders(uipAccessToken),
    body: JSON.stringify({
      other_user_id: secondUserID,
      other_user_type: secondUserType,
      caller_user_id: firstUserID,
      caller_user_type: firstUserType,
    }),
  });
  const conversationData = await getConversationResponse.json();

  return conversationData;
};

/**
 * Get UIP credentials for a bot via iapp-api.
 * Returns a legacy messaging key plus a short-lived user-bound stream token.
 */
export const getUIPCredentials = async (
  botId: string,
): Promise<{
  workspace_id: string;
  uip_token: string;
  uip_user_token: string;
  uip_bot_id: string;
  bot_name: string;
} | null> => {
  try {
    const uipTokenQuery = `uip-token?bot_id=${botId}`;
    const uipResponse = await makeRequest("virtualAdvisors").get(uipTokenQuery, true, false);
    const uipData = uipResponse?.data?.data ?? uipResponse?.data;

    if (uipData?.workspace_id && uipData?.uip_token && uipData?.uip_user_token) {
      // Store credentials for later use
      setLocalStore("uipWorkspaceId", String(uipData.workspace_id));
      setLocalStore("uipAccessToken", String(uipData.uip_token));
      if (uipData?.uip_bot_id) {
        setLocalStore("uipBotId", String(uipData.uip_bot_id));
      }
      return uipData;
    }

    return null;
  } catch (error) {
    console.error("Failed to get UIP credentials:", error);
    return null;
  }
};

/**
 * @deprecated Use getUIPCredentials for new UIP integration.
 * Legacy NEIO V1 auth token retrieval (kept for backward compatibility).
 */
export const setNeioAccessToken = async (): Promise<string | null> => {
  // UIP-enabled builds do not use the legacy NeIO OAuth token. Avoid making
  // the deprecated auth request because its failure must not block Hive data.
  if (process.env.REACT_APP_UIP_ENABLED === "true") {
    removeLocalStore("neioAccessToken");
    return null;
  }

  // Use the normal baseURL with the neioAuthToken path
  const neioAuthQuery = `neio/auth-token`;
  const neioResponse = await makeRequest("virtualAdvisors").get(neioAuthQuery, true, false);
  const neioAccessTokenData = neioResponse?.data?.access_token;

  if (typeof neioAccessTokenData === "string" && neioAccessTokenData.length > 0) {
    setLocalStore("neioAccessToken", neioAccessTokenData);
    return neioAccessTokenData;
  }

  removeLocalStore("neioAccessToken");
  return null;
};

/**
 * Fetch the primary active chat bot (HEY IAPP/root) for default chat flows.
 */
export const getPrimaryChatBot = async (): Promise<any | null> => {
  try {
    const query = `pageNumber=1&pageSize=10&sortBy=createdAt&sortOrder=desc`;
    const response = await makeRequest("virtualAdvisors").get(query, false, true);
    const bots = response?.data?.data || [];
    return bots.find((item: any) => item.isChat && item.isActive) || null;
  } catch (error) {
    return null;
  }
};

/**
 * Resolve UIP credentials for the primary chat bot.
 */
export const getPrimaryUIPCredentials = async () => {
  const bot = await getPrimaryChatBot();
  if (!bot?.id) return null;
  return getUIPCredentials(bot.id);
};

/**
 * Ensure UIP access token is available.
 * First tries to get from localStorage, then falls back to refreshing via UIP token endpoint.
 */
export const ensureUIPAccessToken = async (botId?: string): Promise<string | null> => {
  // First check if we have a cached token
  const storedToken = getObjectFromLocalStore("uipAccessToken");
  if (typeof storedToken === "string" && storedToken.length > 0) {
    return storedToken;
  }

  // If botId provided, try to get new credentials
  if (botId) {
    const credentials = await getUIPCredentials(botId);
    if (credentials?.uip_token) {
      return credentials.uip_token;
    }
  }

  return null;
};

/**
 * @deprecated Use ensureUIPAccessToken for new UIP integration.
 * Legacy NEIO V1 token retrieval (kept for backward compatibility).
 */
export const ensureNeioAccessToken = async (): Promise<string | null> => {
  try {
    const refreshed = await setNeioAccessToken();
    if (refreshed) {
      return refreshed;
    }
  } catch (error) {
    // Ignore and fall back to cached token
  }

  const storedToken = getObjectFromLocalStore("neioAccessToken");
  if (typeof storedToken === "string" && storedToken.length > 0) {
    return storedToken;
  }

  return null;
};

/**
 * Create a new conversation via UIP API.
 * Uses the same endpoint as checkChatConversationExists (get-or-create).
 */
export const createChatConversation = async (
  firstUserID?: string,
  secondUserID?: string,
  uipAccessToken?: string,
  title?: string,
  firstUserType: string = "user",
  secondUserType: string = "bot",
) => {
  const workspaceId = getUIPWorkspaceId();
  if (!workspaceId) {
    console.error("UIP workspace ID not available");
    return { error: "Workspace ID not configured" };
  }

  // UIP uses POST /messaging/conversations/direct to get-or-create
  const url = `${NEIO_CORE_API_URL}/messaging/conversations/direct?workspace_id=${workspaceId}`;
  const createConversationResponse = await fetch(url, {
    method: "POST",
    headers: buildUIPAuthHeaders(uipAccessToken),
    body: JSON.stringify({
      other_user_id: secondUserID,
      other_user_type: secondUserType,
      caller_user_id: firstUserID,
      caller_user_type: firstUserType,
    }),
  }).then((res) => res.json());

  return createConversationResponse;
};

/**
 * Check if two users have blocked each other via UIP API.
 * Uses GET /messaging/users/check-block/{user1_id}/{user2_id} endpoint.
 */
export const checkConversationIsBlocked = async (userId1: string, userId2: string) => {
  const workspaceId = getUIPWorkspaceId();
  if (!workspaceId) {
    console.error("UIP workspace ID not available");
    return { is_blocked: false };
  }

  const uipToken = getObjectFromLocalStore("uipAccessToken");
  const url = `${NEIO_CORE_API_URL}/messaging/users/check-block/${userId1}/${userId2}?workspace_id=${workspaceId}`;
  const checkBlockResponse = await fetch(url, {
    method: "GET",
    headers: buildUIPAuthHeaders(typeof uipToken === "string" ? uipToken : null),
  });
  const blockData = await checkBlockResponse.json();

  return blockData;
};

/**
 * Block a user via UIP API.
 * Uses POST /messaging/users/block endpoint.
 * Note: conversationId is not used in UIP - blocking is user-level, not conversation-level.
 */
export const blockConversation = async (
  firstUserID: string,
  secondUserID: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _conversationId: string, // Kept for backward compatibility, not used in UIP
) => {
  const workspaceId = getUIPWorkspaceId();
  if (!workspaceId) {
    console.error("UIP workspace ID not available");
    return { error: "Workspace ID not configured" };
  }

  const uipToken = getObjectFromLocalStore("uipAccessToken");
  const url = `${NEIO_CORE_API_URL}/messaging/users/block?workspace_id=${workspaceId}`;
  const blockUserResponse = await fetch(url, {
    method: "POST",
    headers: buildUIPAuthHeaders(typeof uipToken === "string" ? uipToken : null),
    body: JSON.stringify({
      blocked_id: secondUserID,
    }),
  });
  const blockData = await blockUserResponse.json();

  return blockData;
};

/**
 * Unblock a user via UIP API.
 * Uses DELETE /messaging/users/block/{blocked_id} endpoint.
 * Note: conversationId is not used in UIP - blocking is user-level, not conversation-level.
 */
export const unblockConversation = async (
  userId1: string,
  userId2: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _conversationId: string, // Kept for backward compatibility, not used in UIP
) => {
  const workspaceId = getUIPWorkspaceId();
  if (!workspaceId) {
    console.error("UIP workspace ID not available");
    return { error: "Workspace ID not configured" };
  }

  const uipToken = getObjectFromLocalStore("uipAccessToken");
  const url = `${NEIO_CORE_API_URL}/messaging/users/block/${userId2}?workspace_id=${workspaceId}`;
  const unblockUserResponse = await fetch(url, {
    method: "DELETE",
    headers: buildUIPAuthHeaders(typeof uipToken === "string" ? uipToken : null),
  });

  // DELETE returns 204 No Content on success
  if (unblockUserResponse.status === 204) {
    return { status: "unblocked", unblocked_id: userId2 };
  }

  const errorData = await unblockUserResponse.json();
  return errorData;
};

export const getChartLabels = (data: any[], selectedChartFilter: string) => {
  if (selectedChartFilter === "insuredAge") {
    return data?.map((item: any) => `A${item?.insuredAge}`);
  }
  if (selectedChartFilter === "planYear") {
    return data?.map((item: any) => item?.planYear);
  }
  return data?.map((item: any) => {
    const curlyApostrophe = "\u2019"; // ’
    // If year is a number like 2025, return last two digits as string
    if (typeof item?.year === "number") {
      return curlyApostrophe + String(item.year).slice(-2);
    }
    // If year is a string, also slice last two digits
    if (typeof item?.year === "string") {
      return curlyApostrophe + item.year.slice(-2);
    }
    return item?.year;
  });
};
